import { useEffect } from 'react';

/**
 * ThemeProvider — pure system-preference theme.
 *
 * Behavior:
 *   - Reads OS preference via prefers-color-scheme media query
 *   - Sets <html data-theme="light"> or <html data-theme="dark"> on mount
 *   - Listens for live OS theme changes (e.g. macOS auto-switch at sunset)
 *   - No user-facing toggle, no localStorage; OS is the single source of truth
 *
 * Tokens defined in src/styles/tokens.css under [data-theme="dark"] activate
 * automatically. Components using var(--bg-primary), var(--text-primary), etc.
 * get themed for free. Components using hardcoded hex are unaffected until
 * they're migrated.
 */
export default function ThemeProvider({ children }) {
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');

    const apply = (isDark) => {
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    };

    // Initial state
    apply(mql.matches);

    // Listen for live OS changes (modern browsers use 'change' event)
    const handler = (e) => apply(e.matches);
    mql.addEventListener('change', handler);

    return () => mql.removeEventListener('change', handler);
  }, []);

  return children;
}
